import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import './EnterpriseAI.css';

const IconChip = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="7" y="7" width="10" height="10" rx="2"/>
    <path d="M7 9H5M7 12H5M7 15H5M17 9h2M17 12h2M17 15h2M9 7V5M12 7V5M15 7V5M9 17v2M12 17v2M15 17v2"/>
  </svg>
);
const IconRobot = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="8" width="18" height="13" rx="2"/>
    <path d="M9 11h.01M15 11h.01M9 15h6M12 8V5M9.5 5h5"/>
  </svg>
);
const IconBolt = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const IconLayers = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
    <polyline points="2 17 12 22 22 17"/>
    <polyline points="2 12 12 17 22 12"/>
  </svg>
);
const IconRefresh = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/>
    <polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
  </svg>
);
const IconGear = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
);

const services = [
  {
    icon: <IconChip />,
    title: 'AI Workflow Integration',
    titleKey: 'agency.ai_service_workflow_title',
    desc: 'We embed AI directly into your existing business processes - automating repetitive tasks, accelerating decisions, and surfacing insights where your team already works.',
    descKey: 'agency.ai_service_workflow_desc',
    color: 'green',
  },
  {
    icon: <IconRobot />,
    title: 'Agentic AI Solutions',
    titleKey: 'agency.ai_service_agentic_title',
    desc: 'Autonomous AI agents that reason, plan, and act on your behalf - handling complex multi-step tasks across systems without constant human intervention.',
    descKey: 'agency.ai_service_agentic_desc',
    color: 'orange',
  },
  {
    icon: <IconBolt />,
    title: 'Web & Process Automation',
    titleKey: 'agency.ai_service_automation_title',
    desc: 'Intelligent automation across web applications, internal portals, and back-office workflows - reducing manual effort and eliminating bottlenecks at scale.',
    descKey: 'agency.ai_service_automation_desc',
    color: 'teal',
  },
  {
    icon: <IconLayers />,
    title: 'Enterprise AI Platform (EAP)',
    titleKey: 'agency.ai_service_platform_title',
    desc: 'A unified platform for deploying, managing, and governing AI models across your organization - with built-in security, observability, and compliance controls.',
    descKey: 'agency.ai_service_platform_desc',
    color: 'green',
  },
  {
    icon: <IconRefresh />,
    title: 'Continuous AI Optimization',
    titleKey: 'agency.ai_service_optimization_title',
    desc: 'Model monitoring, retraining pipelines, and performance benchmarking to ensure your AI investments keep improving as your data and needs evolve.',
    descKey: 'agency.ai_service_optimization_desc',
    color: 'orange',
  },
  {
    icon: <IconGear />,
    title: 'Custom Model Development',
    titleKey: 'agency.ai_service_custom_model_title',
    desc: 'Fine-tuned and purpose-built AI models trained on your proprietary data - delivering domain-specific intelligence that off-the-shelf solutions cannot match.',
    descKey: 'agency.ai_service_custom_model_desc',
    color: 'teal',
  },
];

const benefits = [
  { num: '01', label: 'Faster Time-to-Value',      labelKey: 'agency.ai_benefit_faster_title',   desc: 'Deploy AI solutions in weeks, not months, with our proven implementation frameworks.', descKey: 'agency.ai_benefit_faster_desc' },
  { num: '02', label: 'Enterprise-Grade Security',  labelKey: 'agency.ai_benefit_security_title', desc: 'Every AI deployment is governed with security, compliance, and data privacy at its core.', descKey: 'agency.ai_benefit_security_desc' },
  { num: '03', label: 'Scalable Architecture',      labelKey: 'agency.ai_benefit_scalable_title', desc: 'Built to grow - from a single workflow to organization-wide AI transformation.', descKey: 'agency.ai_benefit_scalable_desc' },
  { num: '04', label: 'Human-in-the-Loop Control',  labelKey: 'agency.ai_benefit_human_title',    desc: 'Maintain full oversight and auditability over every automated decision and action.', descKey: 'agency.ai_benefit_human_desc' },
];

export default function EnterpriseAI() {
  const { t } = useTranslation();
  return (
    <div className="eai-page">

      {/* HERO - matches all other pages */}
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">{t('agency.ai_hero_badge', 'Intelligent Automation')}</span>
          <h1>
            {t('agency.ai_hero_title', 'Enterprise')}{' '}
            <span style={{ color: '#d5772d' }}>
              {t('agency.ai_hero_highlight_title', 'AI Platform')}
            </span>
          </h1>
          <p>
            {t('agency.ai_hero_desc', 'We help clients embed AI directly into their custom workflows - from autonomous agents that act across systems to a full Enterprise AI Platform (EAP) that governs, scales, and secures AI adoption across your entire organization.')}
          </p>
        </div>
      </section>

      {/* SERVICES - light mode */}
      <section className="eai-services" id="services">
        <div className="container">
          <div className="eai-section-header">
            <span className="section-label">{t('agency.ai_services_badge', 'What We Deliver')}</span>
            <h2 className="section-title">{t('agency.ai_services_title', 'Six Core')} <span>{t('agency.ai_services_highlight_title', 'AI Capabilities')}</span></h2>
            <p className="eai-section-desc">
              {t('agency.ai_services_desc', 'End-to-end AI services designed for enterprise scale - from strategy and development to deployment and ongoing optimization.')}
            </p>
          </div>
          <div className="eai-services-grid">
            {services.map((svc, i) => (
              <div className={`eai-card eai-card--${svc.color}`} key={i}>
                <div className={`eai-card-icon eai-card-icon--${svc.color}`}>{svc.icon}</div>
                <h3 className="eai-card-title">{t(svc.titleKey, svc.title)}</h3>
                <p className="eai-card-desc">{t(svc.descKey, svc.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS - light mode */}
      <section className="eai-benefits">
        <div className="container eai-benefits-inner">
          <div className="eai-benefits-left">
            <span className="section-label">{t('agency.ai_benefits_badge', 'Why ThirdEye AI')}</span>
            <h2 className="section-title">{t('agency.ai_benefits_title', 'Built for')} <span>{t('agency.ai_benefits_highlight_title', 'Enterprise Trust')}</span></h2>
            <p className="eai-benefits-desc">
              {t('agency.ai_benefits_desc', 'Our AI platform is not just powerful - it is governed. Every solution we build is designed with security, compliance, and human oversight woven in from day one.')}
            </p>
            <div className="eai-benefits-img">
              <img src="/images/enterprise-ai.jpg" alt={t('agency.ai_benefits_img_alt', 'Enterprise AI platform')} />
            </div>
          </div>
          <div className="eai-benefits-right">
            {benefits.map((b) => (
              <div className="eai-benefit-card" key={b.num}>
                <div className="eai-benefit-num">{b.num}</div>
                <div>
                  <h4>{t(b.labelKey, b.label)}</h4>
                  <p>{t(b.descKey, b.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="eai-cta-section">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-glow" />
            <div className="cta-content">
              <h2>{t('agency.ai_cta_title', 'Ready to Transform Your Business with AI?')}</h2>
              <p>{t('agency.ai_cta_desc', 'Let us map out an AI roadmap tailored to your organization workflows, data, and growth objectives.')}</p>
              <div className="cta-actions">
                <Link to="/it-services" className="btn-outline-white">{t('agency.ai_view_it_services_btn', 'View IT Services')}</Link>
                <Link to="/contact" className="btn-primary">
                  {t('agency.ai_book_discovery_call_btn', 'Book a Discovery Call')} <IconArrow />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
